import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── All possible client fields (shown conditionally per service) ───────────────
export const CLIENT_FIELD_DEFS = [
  { key: 'name', label: 'Name *',           type: 'text', placeholder: 'Full name'    },
  { key: 'dob',  label: 'Date of Birth *',  type: 'date', placeholder: ''             },
  { key: 'tob',  label: 'Time of Birth',    type: 'time', placeholder: ''             },
  { key: 'pob',  label: 'Place of Birth *', type: 'text', placeholder: 'City, State'  },
];

// Fields that must be non-empty before the action button is enabled
export const MANDATORY_FIELDS = { name: true, dob: true, pob: true };

// ── Image compression helper (max 800×800, JPEG 0.8) ─────────────────────────
function compressImageFile(file) {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const MAX = 800;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else                { width  = Math.round(width  * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => resolve(null);
      img.src = ev.target.result;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

// ── Dual palm photo upload widget ────────────────────────────────────────────
const PalmPhotoPanel = ({ extraData, setExtraData, onAnalyzeRightHand, onAnalyzeLeftHand }) => {
  const [expandRight, setExpandRight] = useState(false);
  const [expandLeft,  setExpandLeft]  = useState(false);
  const rightInputCamRef = useRef(null);
  const rightInputGalRef = useRef(null);
  const leftInputCamRef  = useRef(null);
  const leftInputGalRef  = useRef(null);

  const handleFile = useCallback(async (file, hand) => {
    if (!file) return;
    const dataUrl = await compressImageFile(file);
    if (!dataUrl) return;
    const base64 = dataUrl.split(',')[1];
    const key    = hand === 'right' ? 'rightHandPhoto' : 'leftHandPhoto';
    const urlKey = hand === 'right' ? 'rightHandPreviewUrl' : 'leftHandPreviewUrl';
    setExtraData(d => ({ ...d, [key]: file, [urlKey]: dataUrl }));
    if (hand === 'right' && onAnalyzeRightHand) onAnalyzeRightHand(base64, 'image/jpeg');
    if (hand === 'left'  && onAnalyzeLeftHand)  onAnalyzeLeftHand(base64, 'image/jpeg');
  }, [setExtraData, onAnalyzeRightHand, onAnalyzeLeftHand]);

  const rightUrl = extraData.rightHandPreviewUrl;
  const leftUrl  = extraData.leftHandPreviewUrl;

  return (
    <div className="ap-extra ap-palm-panel">
      <div className="ap-palm-panel__title">Palm Photos</div>

      {/* ── RIGHT HAND ── */}
      <div className="ap-palm-hand">
        <div className="ap-palm-hand__header">
          <span className="ap-palm-hand__label">✋ Right Hand</span>
          <span className="ap-palm-hand__sub">Present Life</span>
        </div>
        {rightUrl && (
          <div className="ap-palm-hand__preview" onClick={() => setExpandRight(v => !v)}>
            <img src={rightUrl} alt="Right hand" className={`ap-palm-hand__img${expandRight ? ' ap-palm-hand__img--expanded' : ''}`} />
            <span className="ap-palm-hand__badge ap-palm-hand__badge--ok">✓</span>
          </div>
        )}
        <div className="ap-palm-hand__btns">
          <label className="ap-palm-hand__btn ap-palm-hand__btn--camera">
            <span>📷 Camera</span>
            <input ref={rightInputCamRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0], 'right')} />
          </label>
          <label className="ap-palm-hand__btn ap-palm-hand__btn--gallery">
            <span>🖼 Gallery</span>
            <input ref={rightInputGalRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0], 'right')} />
          </label>
        </div>
        {!rightUrl && <p className="ap-palm-hand__hint">Required — opens camera on mobile</p>}
      </div>

      {/* ── LEFT HAND ── */}
      <div className="ap-palm-hand ap-palm-hand--left">
        <div className="ap-palm-hand__header">
          <span className="ap-palm-hand__label">🤚 Left Hand</span>
          <span className="ap-palm-hand__sub">Birth Potential</span>
        </div>
        {leftUrl && (
          <div className="ap-palm-hand__preview" onClick={() => setExpandLeft(v => !v)}>
            <img src={leftUrl} alt="Left hand" className={`ap-palm-hand__img${expandLeft ? ' ap-palm-hand__img--expanded' : ''}`} />
            <span className="ap-palm-hand__badge ap-palm-hand__badge--ok">✓</span>
          </div>
        )}
        <div className="ap-palm-hand__btns">
          <label className="ap-palm-hand__btn ap-palm-hand__btn--camera">
            <span>📷 Camera</span>
            <input ref={leftInputCamRef} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0], 'left')} />
          </label>
          <label className="ap-palm-hand__btn ap-palm-hand__btn--gallery">
            <span>🖼 Gallery</span>
            <input ref={leftInputGalRef} type="file" accept="image/*"
              style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0], 'left')} />
          </label>
        </div>
        {!leftUrl && <p className="ap-palm-hand__hint">Optional — compare with right hand</p>}
      </div>

      <p className="ap-palm-hint" style={{ marginTop: '0.5rem' }}>
        Photos compressed &amp; analyzed by Claude AI (Sonnet). Tap thumbnail to expand.
      </p>
    </div>
  );
};

// ── Left Panel ─────────────────────────────────────────────────────────────────
const LeftPanel = ({
  clientData, onUpdateClientData,
  config, svcReq, extraData, setExtraData,
  aiData, aiLoading, aiError,
  onAnalyze, onAnalyzeRightHand, onAnalyzeLeftHand,
}) => {
  const [p2open,   setP2open]   = useState(false);
  const [editMode, setEditMode] = useState(!clientData?.name);

  const prevAiData = useRef(aiData);
  useEffect(() => {
    if (!prevAiData.current && aiData) setEditMode(false);
    prevAiData.current = aiData;
  }, [aiData]);

  const setP2 = (k, v) => setExtraData(d => ({ ...d, person2: { ...d.person2, [k]: v } }));

  const mandatoryForService = (svcReq?.fields || ['name']).filter(f => MANDATORY_FIELDS[f]);
  const hasEnoughToStart    = mandatoryForService.every(f => !!clientData?.[f]);
  const visibleFields       = CLIENT_FIELD_DEFS.filter(f => svcReq?.fields.includes(f.key));
  const canCancel           = !!clientData?.name;

  return (
    <aside className="ap-col ap-col--left">

      {/* Service badge */}
      <div className="ap-left__svc-badge">{config.icon} {config.label}</div>

      {editMode ? (
        <div className="ap-new-client-form">
          <div className="ap-new-client-form__header">
            <span className="ap-new-client-form__title">
              {clientData?.name ? 'Edit Client Details' : 'Client Details'}
            </span>
            {canCancel && (
              <button className="ap-new-client-form__cancel" onClick={() => setEditMode(false)}>✕</button>
            )}
          </div>

          {visibleFields.map(({ key, label, type, placeholder }) => (
            <div key={key} className="ap-new-client-form__field">
              <label className="ap-new-client-form__label">{label}</label>
              <input
                className="ap-new-client-form__input"
                type={type}
                placeholder={placeholder}
                value={clientData[key] || ''}
                onChange={e => onUpdateClientData({ [key]: e.target.value })}
              />
            </div>
          ))}

          {(svcReq?.fields.includes('gender') || svcReq?.fields.includes('language')) && (
            <div className="ap-new-client-form__row">
              {svcReq?.fields.includes('gender') && (
                <div className="ap-new-client-form__field ap-new-client-form__field--half">
                  <label className="ap-new-client-form__label">Gender</label>
                  <select
                    className="ap-new-client-form__input"
                    value={clientData.gender || 'Male'}
                    onChange={e => onUpdateClientData({ gender: e.target.value })}
                  >
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
              )}
              {svcReq?.fields.includes('language') && (
                <div className="ap-new-client-form__field ap-new-client-form__field--half">
                  <label className="ap-new-client-form__label">Language</label>
                  <select
                    className="ap-new-client-form__input"
                    value={clientData.language || 'Telugu'}
                    onChange={e => onUpdateClientData({ language: e.target.value })}
                  >
                    {['Telugu','Tamil','Kannada','Hindi','Malayalam','English'].map(l => (
                      <option key={l}>{l}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {canCancel && (
            <button className="ap-new-client-form__done" onClick={() => setEditMode(false)}>
              ✓ Done
            </button>
          )}
        </div>
      ) : (
        <div className="ap-client-card">
          <div className="ap-client-card__avatar">{(clientData.name || '?').charAt(0).toUpperCase()}</div>
          <div className="ap-client-card__info">
            <div className="ap-client-card__name">{clientData.name}</div>
            {clientData.dob && (
              <div className="ap-client-card__birth">
                {clientData.dob}{clientData.tob ? ` · ${clientData.tob}` : ''}
              </div>
            )}
            {clientData.pob && (
              <div className="ap-client-card__birth">{clientData.pob}</div>
            )}
            <div className="ap-client-card__tags">
              {clientData.gender   && <span className="ap-tag">{clientData.gender}</span>}
              {clientData.language && <span className="ap-tag">{clientData.language}</span>}
            </div>
          </div>
          <button className="ap-client-card__edit" onClick={() => setEditMode(true)} title="Edit details">✏</button>
        </div>
      )}

      {/* ── Extra fields per service ── */}

      {config.extraFields?.includes('fullName') && (
        <div className="ap-extra">
          <label className="ap-extra__label">Full Name (as per birth certificate)</label>
          <input className="ap-extra__input" type="text" placeholder="Complete birth name"
            value={extraData.fullName}
            onChange={e => setExtraData(d => ({ ...d, fullName: e.target.value }))} />
        </div>
      )}

      {config.extraFields?.includes('numerologyControls') && (
        <div className="ap-extra">
          <label className="ap-extra__label">Current Name (if different)</label>
          <input className="ap-extra__input" type="text" placeholder="Current used name"
            value={extraData.currentName || ''}
            onChange={e => setExtraData(d => ({ ...d, currentName: e.target.value }))} />
        </div>
      )}

      {config.extraFields?.includes('tarotControls') && (
        <>
          <div className="ap-extra">
            <div className="ap-new-client-form__row">
              <div className="ap-new-client-form__field ap-new-client-form__field--half">
                <label className="ap-extra__label">Area</label>
                <select className="ap-extra__input"
                  value={extraData.tarotArea || 'General'}
                  onChange={e => setExtraData(d => ({ ...d, tarotArea: e.target.value }))}>
                  {['General','Love','Career','Finance','Health','Spiritual'].map(a => <option key={a}>{a}</option>)}
                </select>
              </div>
              <div className="ap-new-client-form__field ap-new-client-form__field--half">
                <label className="ap-extra__label">Time Frame</label>
                <select className="ap-extra__input"
                  value={extraData.tarotTimeframe || 'Current situation'}
                  onChange={e => setExtraData(d => ({ ...d, tarotTimeframe: e.target.value }))}>
                  {['Current situation','Next 3 months','Next 1 year'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {config.extraFields?.includes('clientQuestion') && (
        <div className="ap-extra">
          <label className="ap-extra__label">Client's Question</label>
          <textarea className="ap-extra__textarea" rows={3}
            placeholder="What is the client asking about?"
            value={extraData.clientQuestion}
            onChange={e => setExtraData(d => ({ ...d, clientQuestion: e.target.value }))} />
        </div>
      )}

      {config.extraFields?.includes('palmPhotos') && (
        <PalmPhotoPanel
          extraData={extraData}
          setExtraData={setExtraData}
          onAnalyzeRightHand={onAnalyzeRightHand}
          onAnalyzeLeftHand={onAnalyzeLeftHand}
        />
      )}

      {config.extraFields?.includes('vastuControls') && (
        <>
          <div className="ap-extra">
            <div className="ap-new-client-form__row">
              <div className="ap-new-client-form__field ap-new-client-form__field--half">
                <label className="ap-extra__label">Main Door Direction</label>
                <select className="ap-extra__input"
                  value={extraData.doorDirection || 'North'}
                  onChange={e => setExtraData(d => ({ ...d, doorDirection: e.target.value }))}>
                  {['North','South','East','West','North-East','North-West','South-East','South-West'].map(dir => <option key={dir}>{dir}</option>)}
                </select>
              </div>
              <div className="ap-new-client-form__field ap-new-client-form__field--half">
                <label className="ap-extra__label">Property Type</label>
                <select className="ap-extra__input"
                  value={extraData.propertyType || 'Home'}
                  onChange={e => setExtraData(d => ({ ...d, propertyType: e.target.value }))}>
                  {['Home','Office','Shop','Plot','Flat'].map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="ap-extra">
            <label className="ap-extra__label">Room / Area Concerned</label>
            <input className="ap-extra__input" type="text" placeholder="e.g. Master bedroom, Kitchen..."
              value={extraData.roomConcerned || ''}
              onChange={e => setExtraData(d => ({ ...d, roomConcerned: e.target.value }))} />
          </div>
          <div className="ap-extra">
            <label className="ap-extra__label">Current Problems (optional)</label>
            <textarea className="ap-extra__textarea" rows={2}
              placeholder="e.g. financial loss, health issues, relationship problems..."
              value={extraData.problems || ''}
              onChange={e => setExtraData(d => ({ ...d, problems: e.target.value }))} />
          </div>
        </>
      )}

      {config.extraFields?.includes('compatPurpose') && (
        <div className="ap-extra">
          <label className="ap-extra__label">Purpose</label>
          <select className="ap-extra__input"
            value={extraData.purpose || 'Marriage'}
            onChange={e => setExtraData(d => ({ ...d, purpose: e.target.value }))}>
            {['Marriage','Business','Friendship'].map(p => <option key={p}>{p}</option>)}
          </select>
        </div>
      )}

      {config.extraFields?.includes('reportType') && (
        <div className="ap-extra">
          <label className="ap-extra__label">Report Type</label>
          <select className="ap-extra__input"
            value={extraData.reportType || 'Detailed'}
            onChange={e => setExtraData(d => ({ ...d, reportType: e.target.value }))}>
            {['Basic','Detailed','Premium'].map(r => <option key={r}>{r}</option>)}
          </select>
        </div>
      )}

      {config.extraFields?.includes('housePhoto') && (
        <>
          <div className="ap-extra">
            <label className="ap-extra__label">House Photo</label>
            <label className="ap-file-btn">
              <span>{extraData.housePhoto ? `✓ ${extraData.housePhoto.name}` : '🏠 Upload house photo'}</span>
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => setExtraData(d => ({ ...d, housePhoto: e.target.files[0] || null }))} />
            </label>
          </div>
          <div className="ap-extra">
            <label className="ap-extra__label">Floor Plan (optional)</label>
            <label className="ap-file-btn">
              <span>{extraData.floorPlan ? `✓ ${extraData.floorPlan.name}` : '📐 Upload floor plan'}</span>
              <input type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                onChange={e => setExtraData(d => ({ ...d, floorPlan: e.target.files[0] || null }))} />
            </label>
          </div>
        </>
      )}

      {config.extraFields?.includes('person2') && (
        <div className="ap-extra">
          <button className="ap-p2-toggle" onClick={() => setP2open(v => !v)}>
            {p2open ? '▲' : '▼'} Person 2 Details
          </button>
          {p2open && (
            <div className="ap-p2-form">
              {[['name','Name *'], ['dob','Date of Birth'], ['tob','Time of Birth'], ['pob','Place of Birth']].map(([k, lbl]) => (
                <div key={k} className="ap-p2-field">
                  <label className="ap-extra__label">{lbl}</label>
                  <input className="ap-extra__input"
                    type={k === 'dob' ? 'date' : k === 'tob' ? 'time' : 'text'}
                    value={extraData.person2[k]}
                    onChange={e => setP2(k, e.target.value)}
                    placeholder={lbl.replace(' *', '')} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="ap-col__spacer" />

      {/* Action button */}
      <div className="ap-analyze-wrap">
        <button
          className={`ap-analyze-btn${aiData ? ' ap-analyze-btn--done' : ''}`}
          onClick={onAnalyze}
          disabled={aiLoading || !hasEnoughToStart}
        >
          {aiLoading
            ? 'Loading…'
            : aiData
              ? `↺ Re-${svcReq?.btnLabel || 'Analyze'}`
              : `⭐ ${svcReq?.btnLabel || 'Analyze Chart'}`
          }
        </button>
        {!hasEnoughToStart && !aiLoading && (
          <p className="ap-analyze-hint">
            {mandatoryForService.filter(f => !clientData?.[f]).map(f =>
              f === 'name' ? 'name' : f === 'dob' ? 'date of birth' : 'place of birth'
            ).join(' & ')} required
          </p>
        )}
        {hasEnoughToStart && !aiData && !aiLoading && (
          <p className="ap-analyze-hint">
            {svcReq?.needsChart
              ? 'Click to load birth chart and enable predictions'
              : 'Click to start and enable all sections'
            }
          </p>
        )}
        {aiData && !aiLoading && (
          <p className="ap-analyze-hint ap-analyze-hint--ok">
            {svcReq?.needsChart ? '✓ Chart loaded' : '✓ Session started'} — select a topic
          </p>
        )}
        {aiError && <p className="ap-analyze-error">{aiError}</p>}
      </div>

    </aside>
  );
};

export default LeftPanel;
